import { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Paper from "@mui/material/Paper";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import DownloadIcon from "@mui/icons-material/Download";
import PersonAddAlt1OutlinedIcon from "@mui/icons-material/PersonAddAlt1Outlined";
import { useAuth } from "~/auth/AuthProvider";
import { listAllAccessUsers, listDenials } from "~/services/admin/access";
import { useNotify } from "~/shared/ui/feedback/SnackbarProvider";
import { COLORS } from "~/ui/tokens";
import PageHeader from "~/shared/ui/PageHeader";
import AccessMatrixTab from "./matrix/AccessMatrixTab";
import { downloadMatrixCsv } from "./matrix/exportMatrixCsv";
import PersonDrawer from "./PersonDrawer";
import InviteDialog from "./InviteDialog";
import DenialsTab, { denialsFrom } from "./DenialsTab";
import AuditTab from "./AuditTab";

const TABS = ["matriz", "tentativas", "auditoria"];

function TabLabel({ text, count, active }) {
  return (
    <Box component="span" sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      {text}
      {count !== null && count !== undefined && (
        <Box component="span" sx={{ px: 0.9, borderRadius: 5, fontSize: 11, fontWeight: 700, bgcolor: active ? COLORS.primary : "#eceff1", color: active ? "#fff" : COLORS.muted }}>
          {count}
        </Box>
      )}
    </Box>
  );
}

export default function AccessPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const tab = TABS.includes(searchParams.get("aba")) ? searchParams.get("aba") : "matriz";
  const { reload: reloadMe } = useAuth();
  const [matrix, setMatrix] = useState({ users: [], companies: [], total: null, activeAdminsCount: 0 });
  const [denialsTotal, setDenialsTotal] = useState(null);
  const [reloadToken, setReloadToken] = useState(0);
  const [personId, setPersonId] = useState(null);
  const [invite, setInvite] = useState({ open: false, email: "", intent: "invite" });
  const [exporting, setExporting] = useState(false);
  const [auditSubject, setAuditSubject] = useState(null); // { id, name } — ações sobre a pessoa (L21)
  const notify = useNotify();

  useEffect(() => {
    let alive = true;
    listDenials({ from: denialsFrom(), limit: 1, offset: 0 }).then(
      (result) => alive && setDenialsTotal(result.total),
      () => alive && setDenialsTotal(null)
    );
    return () => {
      alive = false;
    };
  }, [reloadToken]);

  const goTab = (next) => setSearchParams(next === "matriz" ? {} : { aba: next }, { replace: true });
  const refresh = () => setReloadToken((n) => n + 1);
  // 403 FORBIDDEN_ROLE: relê o auth/me em segundo plano, sem trocar a página pelo spinner (mensagem e estágio ficam).
  const refreshMe = () => reloadMe({ silent: true });
  // Nomes dos Admins da página carregada (só para o texto); a regra usa activeAdminsCount (base inteira, spec §6.2).
  const activeAdmins = useMemo(
    () => matrix.users.filter((u) => u.role === "admin" && u.status === "active").map((u) => ({ id: u.id, name: u.name || u.email })),
    [matrix.users]
  );
  const people = useMemo(() => matrix.users.map((u) => ({ id: u.id, name: u.name, email: u.email })), [matrix.users]);
  // "Exportar matriz": todas as páginas de 100 (P1-5), inclusive desativados.
  const exportMatrix = async () => {
    setExporting(true);
    try {
      const all = await listAllAccessUsers({ hideDisabled: false });
      downloadMatrixCsv(all.users, all.companies);
    } catch (e) {
      notify({ severity: "error", message: "Não foi possível exportar a matriz. Tente de novo." });
    } finally {
      setExporting(false);
    }
  };

  return (
    <Box sx={{ p: { xs: 2, md: 3 } }}>
      <PageHeader
        title="Acessos"
        description="Quem tem papel no Antecipe e é gestor de qual empresa"
        actions={
          <>
            <Button variant="outlined" startIcon={<DownloadIcon />} disabled={matrix.users.length === 0 || exporting} onClick={exportMatrix}>
              Exportar matriz
            </Button>
            <Button variant="contained" startIcon={<PersonAddAlt1OutlinedIcon />} onClick={() => setInvite({ open: true, email: "", intent: "invite" })}>
              Convidar pessoa
            </Button>
          </>
        }
      />
      <Paper variant="outlined" sx={{ overflow: "hidden" }}>
        <Tabs value={tab} onChange={(_, next) => goTab(next)} sx={{ borderBottom: "1px solid #e5e7eb", px: 1, "& .MuiTabs-indicator": { bgcolor: COLORS.accent } }}>
          <Tab value="matriz" label={<TabLabel text="Matriz de acessos" count={matrix.total} active={tab === "matriz"} />} sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab value="tentativas" label={<TabLabel text="Tentativas negadas" count={denialsTotal} active={tab === "tentativas"} />} sx={{ textTransform: "none", fontWeight: 600 }} />
          <Tab value="auditoria" label={<TabLabel text="Auditoria" count={null} active={tab === "auditoria"} />} sx={{ textTransform: "none", fontWeight: 600 }} />
        </Tabs>
        {/* A matriz fica montada (escondida) para não perder alterações em estágio ao trocar de aba. */}
        <Box hidden={tab !== "matriz"}>
          <AccessMatrixTab reloadToken={reloadToken} onLoaded={setMatrix} onOpenPerson={setPersonId} onForbidden={refreshMe} />
        </Box>
        {tab === "tentativas" && <DenialsTab reloadToken={reloadToken} onInvite={(email, intent = "invite") => setInvite({ open: true, email, intent })} />}
        {tab === "auditoria" && <AuditTab people={people} companies={matrix.companies} initialSubject={auditSubject} />}
      </Paper>
      <PersonDrawer
        userId={personId}
        open={personId !== null}
        onClose={() => setPersonId(null)}
        onChanged={refresh}
        companies={matrix.companies}
        activeAdminsCount={matrix.activeAdminsCount}
        activeAdmins={activeAdmins}
        onForbidden={refreshMe}
        onOpenAudit={(subject) => {
          setPersonId(null);
          setAuditSubject(subject);
          goTab("auditoria");
        }}
      />
      <InviteDialog
        open={invite.open}
        initialEmail={invite.email}
        intent={invite.intent}
        companies={matrix.companies}
        onClose={() => setInvite({ open: false, email: "", intent: "invite" })}
        onInvited={(result, message) => {
          refresh();
          if (message) notify({ severity: "success", message });
        }}
      />
    </Box>
  );
}
